import React, { useState, useCallback } from 'react';
import DataTable from '../../components/common/DataTable';
import { usePagedData } from '../../hooks/usePagedData';
import { inventoryApi } from '../../api/endpoints';
import { InventoryRecordDto, ChannelMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSearch, FiCheck, FiX, FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';

const InventoryPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [syncingFrom, setSyncingFrom] = useState(false);
  const [syncingTo, setSyncingTo] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<InventoryRecordDto>(inventoryApi.list, {
    initialSortBy: 'productTitle',
    initialSortDesc: false,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleChannelFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        channel: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleBackorderFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        isBackordered: value === '' ? undefined : value === 'true',
      }));
    },
    [setFilters]
  );

  const handleSyncFromAmazon = useCallback(async () => {
    setSyncingFrom(true);
    try {
      await inventoryApi.syncFromAmazon();
      toast.success('Synchronizacja stanow z Amazon rozpoczeta');
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad synchronizacji');
    } finally {
      setSyncingFrom(false);
    }
  }, [refresh]);

  const handleSyncToAmazon = useCallback(async () => {
    setSyncingTo(true);
    try {
      await inventoryApi.syncToAmazon();
      toast.success('Synchronizacja stanow do Amazon rozpoczeta');
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad synchronizacji');
    } finally {
      setSyncingTo(false);
    }
  }, [refresh]);

  const startEdit = useCallback((record: InventoryRecordDto) => {
    setEditingId(record.id);
    setEditValue(record.availableQuantity);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue(0);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    try {
      await inventoryApi.update({ id: editingId, availableQuantity: editValue });
      toast.success('Ilosc zaktualizowana');
      setEditingId(null);
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad aktualizacji');
    }
  }, [editingId, editValue, refresh]);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        saveEdit();
      } else if (e.key === 'Escape') {
        cancelEdit();
      }
    },
    [saveEdit, cancelEdit]
  );

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  const columns = [
    {
      key: 'productTitle',
      header: 'Produkt',
      sortable: true,
      render: (row: InventoryRecordDto) => (
        <span className="text-truncate" title={row.productTitle}>
          {row.productTitle.length > 50
            ? row.productTitle.substring(0, 50) + '...'
            : row.productTitle}
        </span>
      ),
    },
    {
      key: 'asin',
      header: 'ASIN',
      sortable: true,
      render: (row: InventoryRecordDto) => row.asin || '-',
    },
    {
      key: 'sku',
      header: 'SKU',
      render: (row: InventoryRecordDto) => row.sku || '-',
    },
    {
      key: 'warehouseName',
      header: 'Magazyn',
      render: (row: InventoryRecordDto) => row.warehouseName || '-',
    },
    {
      key: 'availableQuantity',
      header: 'Dostepne',
      sortable: true,
      render: (row: InventoryRecordDto) => {
        if (editingId === row.id) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <input
                type="number"
                className="form-control form-control-sm"
                style={{ width: '80px' }}
                value={editValue}
                onChange={(e) => setEditValue(Number(e.target.value))}
                onKeyDown={handleEditKeyDown}
                autoFocus
                min={0}
              />
              <button
                className="btn btn-sm btn-icon btn-success"
                onClick={saveEdit}
                title="Zapisz"
              >
                <FiCheck />
              </button>
              <button
                className="btn btn-sm btn-icon btn-secondary"
                onClick={cancelEdit}
                title="Anuluj"
              >
                <FiX />
              </button>
            </div>
          );
        }
        return (
          <span
            className="editable-cell"
            onClick={() => startEdit(row)}
            title="Kliknij aby edytowac"
            style={{ cursor: 'pointer', borderBottom: '1px dashed #999' }}
          >
            {row.availableQuantity}
          </span>
        );
      },
    },
    {
      key: 'reservedQuantity',
      header: 'Zarezerwowane',
      render: (row: InventoryRecordDto) => row.reservedQuantity ?? '-',
    },
    {
      key: 'inboundQuantity',
      header: 'W transporcie',
      render: (row: InventoryRecordDto) => row.inboundQuantity ?? '-',
    },
    {
      key: 'isBackordered',
      header: 'Backorder',
      render: (row: InventoryRecordDto) =>
        row.isBackordered ? (
          <span className="badge badge-danger">Tak</span>
        ) : (
          <span className="badge badge-success">Nie</span>
        ),
    },
    {
      key: 'syncedAt',
      header: 'Synchronizacja',
      sortable: true,
      render: (row: InventoryRecordDto) => formatDate(row.syncedAt),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Stany magazynowe</h1>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.inventoryXlsx, `stany_mag_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSyncFromAmazon}
            disabled={syncingFrom}
          >
            <FiRefreshCw className={syncingFrom ? 'spin' : ''} />
            {syncingFrom ? 'Synchronizacja...' : 'Pobierz z Amazon'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleSyncToAmazon}
            disabled={syncingTo}
          >
            <FiRefreshCw className={syncingTo ? 'spin' : ''} />
            {syncingTo ? 'Synchronizacja...' : 'Wyslij do Amazon'}
          </button>
        </div>
      </div>

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Szukaj produktow..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: 'Wszystkie kanaly' },
              ...Object.entries(ChannelMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.channel ?? '')}
            onChange={handleChannelFilter}
            placeholder="Wszystkie kanaly"
          />

          <Select
            options={[
              { value: '', label: 'Backorder: Wszystkie' },
              { value: 'true', label: 'Tylko backorder' },
              { value: 'false', label: 'Bez backorder' },
            ]}
            value={String(filters.isBackordered ?? '')}
            onChange={handleBackorderFilter}
            placeholder="Backorder: Wszystkie"
          />
        </div>
      </div>

      {loading ? (
        <div className="loader-wrapper">
          <div className="loader" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
        />
      )}
    </div>
  );
};

export default InventoryPage;
